import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import CustomSelectField from '@/components/inputs/CustomSelectField'
import { BidUpdateStatus } from '@/features/bidding/constants'
import type { BidUpdate } from '@/features/bidding/types/bidUpdate'
import { getBidUpdateTime, sortBidUpdatesByTime } from '@/features/bidding/utils/bidUpdateTime'
import { getCreatedByEmoji } from '@/features/bidding/utils/createdBy'

type Props = {
  selectedBidUpdate: BidUpdate
  bidUpdates: BidUpdate[]
  setSelectedBidUpdate: (newBidUpdate: BidUpdate) => void
}

const getStatusEmoji = (status: BidUpdateStatus): string => {
  switch (status) {
    case BidUpdateStatus.PENDING:
      return '⏳'
    case BidUpdateStatus.ACKNOWLEDGED:
      return '✅'
    case BidUpdateStatus.REJECTED:
      return '❌'
    case BidUpdateStatus.FAILED:
      return '⚠️'
    default:
      return ''
  }
}

const BidUpdateVersionSelector: FC<Props> = ({ selectedBidUpdate, bidUpdates, setSelectedBidUpdate }) => {
  const { t } = useTranslation()
  const sortedBidUpdates = sortBidUpdatesByTime(bidUpdates)

  const onVersionChange = (newBidUpdateUuid: string | undefined) => {
    if (!newBidUpdateUuid) return

    const newBidUpdate = bidUpdates.find((bidUpdate) => bidUpdate.uuid === newBidUpdateUuid)

    if (newBidUpdate) {
      setSelectedBidUpdate(newBidUpdate)
    }
  }

  return (
    <CustomSelectField
      id="version-selector"
      label={t('bidding.results.version')}
      options={sortedBidUpdates.map((bidUpdate, index) => {
        const emojiPrefix = [getStatusEmoji(bidUpdate.status), getCreatedByEmoji(bidUpdate.createdBy)]
          .filter(Boolean)
          .join(' ')
        const label = t('bidding.results.bid_version', {
          versionNumber: index + 1,
          date: getBidUpdateTime(bidUpdate)?.toFormat('DD - HH:mm') ?? '',
        })

        return {
          id: bidUpdate.uuid,
          value: bidUpdate.uuid,
          label: emojiPrefix ? `${emojiPrefix} ${label}` : label,
        }
      })}
      size="medium"
      sx={{ minWidth: 90 }}
      value={selectedBidUpdate.uuid}
      onChange={(event) => onVersionChange(event.target.value)}
    />
  )
}

export default BidUpdateVersionSelector
